<template>
  <div class="task-card" :class="`priority-${task.priority}`">
    <div class="card-left">
      <div class="priority-bar"></div>
    </div>
    <div class="card-body">
      <div class="card-top">
        <h3 class="task-title">{{ task.title }}</h3>
        <div class="card-actions">
          <button
            v-if="task.status !== 'done'"
            class="btn btn-ghost btn-sm"
            @click="advance"
            :title="nextStatusLabel"
          >
            {{ nextStatusLabel }} →
          </button>
          <button
            class="btn btn-danger btn-sm"
            @click="confirmDelete"
            :disabled="task.status !== 'done'"
            :title="task.status !== 'done' ? 'Only done tasks can be deleted' : 'Delete task'"
          >
            ✕
          </button>
        </div>
      </div>
      <div class="card-meta">
        <span class="badge" :class="`badge-${task.priority}`">{{ task.priority }}</span>
        <span class="badge" :class="`badge-${task.status}`">{{ formatStatus(task.status) }}</span>
        <span class="due-date" :class="{ overdue: isOverdue }">
          📅 {{ formatDate(task.due_date) }}
          <span v-if="isOverdue" class="overdue-label"> · overdue</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaskCard',
  props: {
    task: { type: Object, required: true }
  },
  emits: ['update-status', 'delete'],
  computed: {
    nextStatus() {
      const map = { pending: 'in_progress', in_progress: 'done' }
      return map[this.task.status] || null
    },
    nextStatusLabel() {
      const map = { pending: 'Start', in_progress: 'Complete' }
      return map[this.task.status] || ''
    },
    isOverdue() {
      if (this.task.status === 'done') return false
      return new Date(this.task.due_date) < new Date(new Date().toDateString())
    }
  },
  methods: {
    advance() {
      if (this.nextStatus) {
        this.$emit('update-status', { id: this.task.id, newStatus: this.nextStatus })
      }
    },
    confirmDelete() {
      if (confirm(`Delete "${this.task.title}"?`)) {
        this.$emit('delete', this.task.id)
      }
    },
    formatStatus(s) {
      return { pending: 'Pending', in_progress: 'In Progress', done: 'Done' }[s] || s
    },
    formatDate(d) {
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.task-card {
  display: flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  animation: slideIn 0.25s ease;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  border-color: var(--accent);
}

.card-left { width: 4px; flex-shrink: 0; }
.priority-high .card-left { background: var(--high); }
.priority-medium .card-left { background: var(--medium); }
.priority-low .card-left { background: var(--low); }

.card-body { flex: 1; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 10px; }

.card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }

.task-title {
  font-family: 'Syne', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.4;
  color: var(--text);
}

.card-actions { display: flex; gap: 6px; flex-shrink: 0; }

.card-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

.due-date {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-left: 4px;
}

.due-date.overdue { color: var(--danger); }
.overdue-label { font-weight: 600; }

@keyframes slideIn {
  from { opacity: 0; transform: translateX(-8px) }
  to { opacity: 1; transform: translateX(0) }
}
</style>